import type { FastifyRequest, FastifyReply } from 'fastify'
import { weatherService } from './weather.service.js'
import { weatherQuerySchema, weatherComparisonSchema } from './weather.schemas.js'
import { db } from '../../core/database/firebase.js'
import { createLogger } from '../../common/utils/logger.js'
import type { FirestoreDoc } from '../../common/types/firestore.js'

const logger = createLogger('WeatherController')

export class WeatherController {
  /**
   * GET /api/weather?lat=XX&lng=XX
   * Get weather for specific location
   */
  async getWeather(req: FastifyRequest, reply: FastifyReply) {
    try {
      const { lat, lng, name } = weatherQuerySchema.parse(req.query)
      const weather = await weatherService.getLocationWeather(lat, lng, { name })
      return reply.code(200).send(weather)
    } catch (error) {
      logger.error('Failed to get weather', { error })
      return reply.code(400).send({ error: 'Invalid request parameters' })
    }
  }

  /**
   * GET /api/weather/route/:planId
   * Analyze weather for entire route
   */
  async getRouteWeather(req: FastifyRequest, reply: FastifyReply) {
    try {
      const { planId } = req.params as { planId: string }
      const userId = req.user?.sub

      if (!userId) {
        return reply.code(401).send({ error: 'Unauthorized' })
      }

      // Fetch plan with stages
      const planDoc = await db.collection('plans').doc(planId).get()
      
      if (!planDoc.exists) {
        return reply.code(404).send({ error: 'Plan not found' })
      }

      const plan = planDoc.data()
      
      if (plan?.userId !== userId) {
        return reply.code(403).send({ error: 'Forbidden' })
      }

      // Fetch stages
      const stagesSnapshot = await db
        .collection('plans')
        .doc(planId)
        .collection('stages')
        .orderBy('order')
        .get()

      const stages = stagesSnapshot.docs.map((doc: FirestoreDoc) => {
        const data = doc.data()
        return {
          id: doc.id,
          name: data.name,
          location: data.location,
          estimatedArrival: data.estimatedArrival
        }
      })

      const analysis = await weatherService.analyzeRouteWeather(planId, stages)
      
      return reply.code(200).send(analysis)
    } catch (error) {
      logger.error('Failed to analyze route weather', { error })
      return reply.code(500).send({ error: 'Internal server error' })
    }
  }

  /**
   * POST /api/weather/compare
   * Compare weather for multiple dates
   */
  async compareWeather(req: FastifyRequest, reply: FastifyReply) {
    try {
      const { lat, lng, dates } = weatherComparisonSchema.parse(req.body)
      const parsedDates = dates.map(d => new Date(d))
      
      const comparison = await weatherService.getWeatherComparison(lat, lng, parsedDates)
      
      return reply.code(200).send({ forecasts: comparison })
    } catch (error) {
      logger.error('Failed to compare weather', { error })
      return reply.code(400).send({ error: 'Invalid request parameters' })
    }
  }
}

export const weatherController = new WeatherController()
